import React, { useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Calculator, TrendingUp, TrendingDown, Clock, AlertTriangle, FileText } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { fmtINR, fmtNum, parseFormattedNumber } from '../lib/utils';
import { LIMITS, PERP_THRESHOLD } from '../lib/constants';

// --- Animation config (constant, outside component) ---

const sectionAnim = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.4, ease: 'easeOut' },
};

// --- Status derivation helpers (extracted to reduce cyclomatic complexity) ---

function buildSustainText(result, targetMultiplier) {
  const sustainYears = result.sustainability_years || PERP_THRESHOLD;
  const isPerp = sustainYears >= PERP_THRESHOLD;
  const multLabel = targetMultiplier === Math.floor(targetMultiplier) 
    ? `${Math.floor(targetMultiplier)}x` 
    : `${targetMultiplier}x`;
  
  // Build achievement text for 50-year multiplier (changed from 100)
  let achievementText = '';
  if (result.multiplier_at_50_years !== null && result.multiplier_at_50_years !== undefined) {
    if (result.multiplier_at_50_years > 0) {
      achievementText = ` (Corpus Reaches ${result.multiplier_at_50_years}x in 50 Years)`;
    }
  }
  
  // If target was reached (corpus grew to target)
  if (result.years_until_target) {
    return { 
      sustainYears, 
      isPerp, 
      text: `Corpus reaches ${multLabel} in ${result.years_until_target} years.${achievementText}` 
    };
  }
  
  // If corpus depletes before reaching target
  if (result.years_until_depletion) {
    return { 
      sustainYears, 
      isPerp, 
      text: `Corpus depletes in ${result.years_until_depletion} years (does not reach ${multLabel}).` 
    };
  }
  
  // Perpetual sustainability - show 100-year multiplier in main text
  if (isPerp && result.multiplier_at_100_years) {
    return { 
      sustainYears, 
      isPerp, 
      text: `Corpus reaches ${result.multiplier_at_100_years}x in 100 years.${achievementText}` 
    };
  }
  
  return { sustainYears, isPerp, text: `Wealth sustains for ${sustainYears} years.${achievementText}` };
}

function deriveStatusInfo(result, corpus, targetMultiplier) {
  if (!result) return null;
  const { sustainYears, isPerp, text: sustainText } = buildSustainText(result, targetMultiplier);

  // Use sustainability_years from backend (accounts for target threshold)
  const yrs = result.sustainability_years || sustainYears;
  
  // NEW COLOR LOGIC: Green if target reached, Red if not reached
  const targetReached = result.years_until_target !== null && result.years_until_target !== undefined;
  
  if (yrs < PERP_THRESHOLD) {
    let color, bgColor, icon;
    
    if (targetReached) {
      // Target REACHED - Always GREEN
      color = 'text-green-600';
      bgColor = 'bg-green-50 border-green-200';
      icon = TrendingUp;
    } else {
      // Target NOT reached (depletes before target) - Always RED
      color = 'text-red-600';
      bgColor = 'bg-red-50 border-red-200';
      icon = TrendingDown;
    }
    
    return {
      type: 'sustainability',
      icon,
      color,
      bgColor,
      label: `${yrs} Years`,
      desc: sustainText,
    };
  }

  // Perpetually sustainable - show "100 Years" with multiplier value (not 500+)
  return {
    type: 'sustainable',
    icon: TrendingUp,
    color: 'text-green-600',
    bgColor: 'bg-green-50 border-green-200',
    label: `100 Years`,  // Changed from 500+ to 100
    desc: sustainText,
  };
}

// --- Sub-components ---

const LoadingSkeleton = () => (
  <div className="animate-pulse space-y-4">
    <div className="h-12 bg-secondary rounded-lg" />
    <div className="h-4 bg-secondary rounded w-3/4" />
    <div className="h-4 bg-secondary rounded w-1/2" />
  </div>
);

const ErrorDisplay = ({ message }) => (
  <div className="flex items-center gap-2 text-destructive">
    <AlertTriangle size={16} />
    <span className="text-sm">{message}</span>
  </div>
);

const ResultDisplay = ({ result, corpus, annualWithdrawal, returnRate, inflationRate, onCalculate }) => {
  if (!result) return null;
  
  const peakYear = result.peak_year || 0;
  const depletionYear = result.years_until_depletion || null;
  const peakBalance = result.peak_balance || corpus;

  const handleDownloadClick = async () => {
    toast.loading('Preparing your sustainability report...');
    const success = await onCalculate?.();
    toast.dismiss();
    if (success) {
      toast.success(
        <div>
          <strong>Report downloaded!</strong>
          <br />
          <small>Check your Downloads folder (Ctrl+J / Cmd+J)</small>
        </div>,
        { duration: 6000 },
      );
    } else if (success === false) {
      toast.error('Failed to generate report. Please try again.');
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Peak and Depletion Side by Side */}
      <div className="rounded-xl border border-border bg-background p-6 shadow-md">
        <div className="grid grid-cols-2 gap-4 divide-x divide-border">
          {/* Legacy Balance - GREEN */}
          <div className="pr-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp size={24} className="text-green-600" />
              <span className="text-sm font-medium text-muted-foreground">Legacy Balance</span>
            </div>
            <div className="text-5xl sm:text-6xl font-extrabold text-green-600 font-mono-num mb-2" data-testid="calculator-peak-years">
              {peakYear} Year{peakYear !== 1 ? 's' : ''}
            </div>
            <div className="text-base font-semibold text-foreground/80">
              {fmtINR(peakBalance)}
            </div>
          </div>
          
          {/* Depletion - RED */}
          <div className="pl-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown size={24} className="text-red-600" />
              <span className="text-sm font-medium text-muted-foreground">Depletes In</span>
            </div>
            {depletionYear ? (
              <>
                <div className="text-5xl sm:text-6xl font-extrabold text-red-600 font-mono-num mb-2" data-testid="calculator-depletion-years">
                  {depletionYear} Year{depletionYear !== 1 ? 's' : ''}
                </div>
                <div className="text-base font-semibold text-foreground/80">
                  Corpus reaches ₹0
                </div>
              </>
            ) : (
              <>
                <div className="text-4xl sm:text-5xl font-extrabold text-green-600 font-mono-num mb-2" data-testid="calculator-depletion-years">
                  Never
                </div>
                <div className="text-base font-semibold text-foreground/80">
                  Perpetually sustainable
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex justify-between items-center py-2 border-b border-border">
          <span className="text-sm text-muted-foreground">Starting Corpus</span>
          <span className="font-mono-num text-sm font-semibold">{fmtINR(corpus)}</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-border">
          <span className="text-sm text-muted-foreground">Year 1 Withdrawal</span>
          <span className="font-mono-num text-sm font-semibold">{fmtINR(annualWithdrawal)}</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-border">
          <span className="text-sm text-muted-foreground">Legacy Balance</span>
          <span className="font-mono-num text-sm font-semibold">
            {result ? fmtINR(result.peak_balance) : '\u2014'}
          </span>
        </div>
        <div className="flex justify-between items-center py-2">
          <span className="text-sm text-muted-foreground">Total Withdrawn</span>
          <span className="font-mono-num text-sm font-semibold">
            {result ? fmtINR(result.total_withdrawn) : '\u2014'}
          </span>
        </div>
      </div>

      <div className="rounded-lg bg-secondary/60 p-3" data-testid="calculator-assumptions-text">
        <p className="text-xs text-muted-foreground">
          Assumes {returnRate}% annual return, {inflationRate}% annual inflation on withdrawals.
          Withdrawals increase every year to maintain purchasing power.
        </p>
      </div>

      {/* PDF Download Button - Right after results */}
      {result && (
        <div className="mt-6 space-y-4">
          <Button 
            onClick={handleDownloadClick}
            className="w-full h-12 bg-[#f15b29] hover:bg-[#d94e20] text-white font-semibold text-base"
            data-testid="download-pdf-button-results"
          >
            <FileText className="mr-2 h-5 w-5" />
            Download Full PDF Report
          </Button>
          
          {/* Disclaimer */}
          <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong>Disclaimer:</strong> This calculator is for illustrative purposes only and does not constitute financial advice. 
              Actual returns may vary based on market conditions. Past performance is not indicative of future results. 
              Please consult with a qualified financial advisor before making investment decisions.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Form section sub-components ---

const CorpusSection = ({ corpusDisplay, handleCorpusInput, corpus, setCorpus }) => {
  const sliderValue = useMemo(() => [corpus], [corpus]);
  const handleSliderChange = useCallback(([v]) => setCorpus(v), [setCorpus]);
  return (
  <div>
    <div className="flex items-center justify-between mb-2">
      <Label htmlFor="corpus-input" className="text-sm font-medium">Total Corpus</Label>
      <span className="text-xs text-muted-foreground">Min: {'₹'}1L | Max: {'₹'}100Cr</span>
    </div>
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">{'₹'}</span>
      <input
        id="corpus-input"
        type="text"
        value={corpusDisplay}
        onChange={handleCorpusInput}
        className="w-full h-10 pl-8 pr-3 rounded-lg border border-input bg-background text-sm font-mono-num focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
        data-testid="calculator-corpus-input"
      />
    </div>
    <Slider
      value={sliderValue}
      onValueChange={handleSliderChange}
      min={LIMITS.CORPUS_MIN}
      max={LIMITS.CORPUS_MAX}
      step={LIMITS.CORPUS_STEP}
      className="mt-3"
      data-testid="calculator-corpus-slider"
      aria-label="Total Corpus"
    />
    <div className="flex justify-between mt-1 text-xs text-muted-foreground">
      <span>{'₹'}1 Lakh</span>
      <span>{'₹'}100 Crore</span>
    </div>
  </div>
  );
};

const WithdrawalSection = ({ withdrawalDisplay, handleWithdrawalInput, monthlyWithdrawal, setMonthlyWithdrawal, annualWithdrawal, corpus, returnRate, inflationRate }) => {
  // Calculate sustainable monthly withdrawal (break-even)
  const netGrowthRate = (returnRate - inflationRate) / 100;
  const sustainableMonthly = Math.round((corpus * netGrowthRate) / 12);
  const isSustainable = monthlyWithdrawal <= sustainableMonthly;
  const sliderValue = useMemo(() => [monthlyWithdrawal], [monthlyWithdrawal]);
  const handleSliderChange = useCallback(([v]) => setMonthlyWithdrawal(v), [setMonthlyWithdrawal]);
  
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label htmlFor="withdrawal-input" className="text-sm font-medium">Monthly Withdrawal</Label>
        <span className="text-xs text-muted-foreground font-mono-num">
          {'₹'}{fmtNum(annualWithdrawal)}/year
        </span>
      </div>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">{'₹'}</span>
        <input
          id="withdrawal-input"
          type="text"
          value={withdrawalDisplay}
          onChange={handleWithdrawalInput}
          className="w-full h-10 pl-8 pr-14 rounded-lg border border-input bg-background text-sm font-mono-num focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
          data-testid="calculator-withdrawal-input"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">/month</span>
      </div>
      <Slider
        value={sliderValue}
        onValueChange={handleSliderChange}
        min={LIMITS.WITHDRAWAL_MIN}
        max={LIMITS.WITHDRAWAL_MAX}
        step={LIMITS.WITHDRAWAL_STEP}
        className="mt-3"
        data-testid="calculator-withdrawal-slider"
        aria-label="Monthly Withdrawal"
      />
      <div className="flex justify-between mt-1 text-xs text-muted-foreground">
        <span>{'₹'}0</span>
        <span>{'₹'}1 Crore</span>
      </div>
      
      {/* Break-even suggestion */}
      <div className={`mt-3 p-2 rounded-md text-xs ${isSustainable ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-amber-50 border border-amber-200 text-amber-700'}`}>
        <span className="font-semibold">💡 Sustainable Withdrawal: </span>
        <span className="font-mono-num font-semibold">{'₹'}{fmtNum(sustainableMonthly)}/month</span>
        <span className="text-[10px] block mt-1">
          {isSustainable 
            ? 'Your withdrawal is within sustainable limits.' 
            : 'Withdrawing above this amount will deplete your corpus over time.'}
        </span>
      </div>
    </div>
  );
};

const AssumptionsSection = ({
  returnRate, setReturnRate, handleReturnInput,
  inflationRate, setInflationRate, handleInflationInput,
}) => {
  const returnSliderValue = useMemo(() => [returnRate], [returnRate]);
  const inflationSliderValue = useMemo(() => [inflationRate], [inflationRate]);
  const handleReturnSliderChange = useCallback(([v]) => setReturnRate(v), [setReturnRate]);
  const handleInflationSliderChange = useCallback(([v]) => setInflationRate(v), [setInflationRate]);
  return (
  <div className="grid grid-cols-2 gap-4">
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label htmlFor="return-input" className="text-sm font-medium">Expected Return</Label>
      </div>
      <div className="relative">
        <input
          id="return-input" type="number" value={returnRate} onChange={handleReturnInput}
          min={LIMITS.RETURN_MIN} max={LIMITS.RETURN_MAX} step={LIMITS.RETURN_STEP}
          className="w-full h-10 pl-3 pr-8 rounded-lg border border-input bg-background text-sm font-mono-num focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
          data-testid="calculator-return-input"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
      </div>
      <Slider
        value={returnSliderValue} onValueChange={handleReturnSliderChange}
        min={LIMITS.RETURN_MIN} max={LIMITS.RETURN_MAX} step={LIMITS.RETURN_STEP}
        className="mt-3" data-testid="calculator-return-slider" aria-label="Expected Return Rate"
      />
      <div className="flex justify-between mt-1 text-xs text-muted-foreground">
        <span>{LIMITS.RETURN_MIN}%</span><span>{LIMITS.RETURN_MAX}%</span>
      </div>
    </div>
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label htmlFor="inflation-input" className="text-sm font-medium">Inflation Rate</Label>
      </div>
      <div className="relative">
        <input
          id="inflation-input" type="number" value={inflationRate} onChange={handleInflationInput}
          min={LIMITS.INFLATION_MIN} max={LIMITS.INFLATION_MAX} step={LIMITS.INFLATION_STEP}
          className="w-full h-10 pl-3 pr-8 rounded-lg border border-input bg-background text-sm font-mono-num focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
          data-testid="calculator-inflation-input"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
      </div>
      <Slider
        value={inflationSliderValue} onValueChange={handleInflationSliderChange}
        min={LIMITS.INFLATION_MIN} max={LIMITS.INFLATION_MAX} step={LIMITS.INFLATION_STEP}
        className="mt-3" data-testid="calculator-inflation-slider" aria-label="Inflation Rate"
      />
      <div className="flex justify-between mt-1 text-xs text-muted-foreground">
        <span>{LIMITS.INFLATION_MIN}%</span><span>{LIMITS.INFLATION_MAX}%</span>
      </div>
    </div>
  </div>
  );
};

// --- Main component ---

export const WealthCalculator = ({
  corpus, setCorpus,
  monthlyWithdrawal, setMonthlyWithdrawal,
  returnRate, setReturnRate,
  inflationRate, setInflationRate,
  result, loading, error,
  onCalculate  // Added for PDF download
}) => {
  const annualWithdrawal = monthlyWithdrawal * 12;
  const corpusDisplay = fmtNum(corpus);
  const withdrawalDisplay = fmtNum(monthlyWithdrawal);

  const handleCorpusInput = useCallback(
    (e) => {
      const val = parseFormattedNumber(e.target.value);
      if (val >= 0 && val <= LIMITS.CORPUS_MAX) setCorpus(val);
    },
    [setCorpus],
  );

  const handleWithdrawalInput = useCallback(
    (e) => {
      const val = parseFormattedNumber(e.target.value);
      if (val >= 0 && val <= LIMITS.WITHDRAWAL_MAX) setMonthlyWithdrawal(val);
    },
    [setMonthlyWithdrawal],
  );

  const handleReturnInput = useCallback(
    (e) => {
      const v = parseFloat(e.target.value);
      if (!isNaN(v) && v >= LIMITS.RETURN_MIN && v <= LIMITS.RETURN_MAX) setReturnRate(v);
    },
    [setReturnRate],
  );

  const handleInflationInput = useCallback(
    (e) => {
      const v = parseFloat(e.target.value);
      if (!isNaN(v) && v >= LIMITS.INFLATION_MIN && v <= LIMITS.INFLATION_MAX) setInflationRate(v);
    },
    [setInflationRate],
  );

  const renderResultContent = () => {
    if (loading) return <LoadingSkeleton />;
    if (error) return <ErrorDisplay message={error} />;
    if (result) {
      return (
        <ResultDisplay
          result={result}
          corpus={corpus}
          annualWithdrawal={annualWithdrawal}
          returnRate={returnRate}
          inflationRate={inflationRate}
          onCalculate={onCalculate}
        />
      );
    }
    return null;
  };

  return (
    <section id="calculator" className="py-12 sm:py-16 lg:py-20 bg-background" data-testid="calculator-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...sectionAnim} className="text-center mb-10">
          <Badge variant="outline" className="mb-3 text-xs font-medium">
            <Calculator size={12} className="mr-1" />
            Interactive Calculator
          </Badge>
          <h2 className="text-2xl sm:text-3xl font-bold text-primary">Wealth Sustainability Calculator</h2>
          <p className="mt-2 text-muted-foreground text-sm sm:text-base max-w-lg mx-auto">
            Adjust the sliders or type values to see how long your savings will last.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Inputs */}
          <motion.div {...sectionAnim} className="lg:col-span-7">
            <Card className="border border-border shadow-md">
              <CardContent className="p-5 sm:p-6 space-y-6">
                <CorpusSection
                  corpusDisplay={corpusDisplay}
                  handleCorpusInput={handleCorpusInput}
                  corpus={corpus}
                  setCorpus={setCorpus}
                />
                <WithdrawalSection
                  withdrawalDisplay={withdrawalDisplay}
                  handleWithdrawalInput={handleWithdrawalInput}
                  monthlyWithdrawal={monthlyWithdrawal}
                  setMonthlyWithdrawal={setMonthlyWithdrawal}
                  annualWithdrawal={annualWithdrawal}
                  corpus={corpus}
                  returnRate={returnRate}
                  inflationRate={inflationRate}
                />
                <AssumptionsSection
                  returnRate={returnRate} setReturnRate={setReturnRate} handleReturnInput={handleReturnInput}
                  inflationRate={inflationRate} setInflationRate={setInflationRate} handleInflationInput={handleInflationInput}
                />
              </CardContent>
            </Card>
          </motion.div>

          {/* Results */}
          <motion.div {...sectionAnim} className="lg:col-span-5">
            <Card className="border border-border shadow-md sticky top-20">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Clock size={16} className="text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">Sustainability Period</span>
                </div>
                {renderResultContent()}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WealthCalculator;
